import { useCallback, useState } from "react";
import { Outlet } from "react-router";

import { AboutDialog } from "@/app/shell/AboutDialog";
import { CommandPalette } from "@/app/shell/CommandPalette";
import { DesktopHeader } from "@/app/shell/DesktopHeader";
import { MobileTopbar } from "@/app/shell/MobileTopbar";
import { ShortcutsDialog } from "@/app/shell/ShortcutsDialog";
import { MobileMenu, Sidebar } from "@/app/shell/Sidebar";
import { useShortcuts } from "@/app/shell/useShortcuts";
import { useSidebarCollapsed } from "@/app/shell/useSidebarCollapsed";
import { NewLeadsNotice } from "@/features/clients/components/NewLeadsNotice";
import { useT } from "@/shared/i18n/useT";

/** The signed-in frame: charcoal rail, floating header, and the white work panel. */
export function AppShell() {
  const t = useT();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [sidebarCollapsed, toggleSidebar] = useSidebarCollapsed();

  const openPalette = useCallback(() => {
    setPaletteOpen(true);
  }, []);
  const togglePalette = useCallback(() => {
    setPaletteOpen((open) => !open);
  }, []);
  const openShortcuts = useCallback(() => {
    setShortcutsOpen(true);
  }, []);
  const openAbout = useCallback(() => {
    setAboutOpen(true);
  }, []);
  useShortcuts({ onPalette: togglePalette, onHelp: openShortcuts });

  const sidebarActions = { onShowShortcuts: openShortcuts, onShowAbout: openAbout };

  return (
    // On desktop only the work panel scrolls; the rail and header never move.
    <div className="flex min-h-dvh bg-canvas lg:h-dvh lg:overflow-hidden">
      <a
        href="#main"
        className="sr-only z-50 rounded-lg bg-ink px-3 py-2 text-sm text-ink-inverse focus:not-sr-only focus:fixed focus:start-3 focus:top-3"
      >
        {t("shell.skipToContent")}
      </a>
      <Sidebar {...sidebarActions} collapsed={sidebarCollapsed} onToggle={toggleSidebar} />
      <div className="flex min-w-0 flex-1 flex-col lg:h-dvh lg:px-3 lg:pt-2 lg:pb-3">
        <DesktopHeader onSearch={openPalette} {...sidebarActions} />
        <MobileTopbar
          onSearch={openPalette}
          onOpenMenu={() => {
            setMenuOpen(true);
          }}
        />
        <main
          id="main"
          className="flex min-h-0 flex-1 flex-col bg-surface lg:overflow-y-auto lg:overscroll-contain lg:rounded-2xl lg:border lg:border-line lg:shadow-sm"
        >
          <Outlet />
        </main>
      </div>
      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
      <ShortcutsDialog open={shortcutsOpen} onOpenChange={setShortcutsOpen} />
      <AboutDialog open={aboutOpen} onOpenChange={setAboutOpen} />
      <MobileMenu open={menuOpen} onOpenChange={setMenuOpen} {...sidebarActions} />
      <NewLeadsNotice />
    </div>
  );
}
